import { useEffect, useState } from "react";
import { Bell, LogOut, Printer, Lock, CalendarDays, HeartPulse, Scale, Paperclip, MessagesSquare } from "lucide-react";
import { ChatBox } from "@/components/ChatBox";
import { AttachmentList } from "@/components/AttachmentList";
import { BookVisitDialog } from "@/components/BookVisitDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate, fmtDateTime, fullName, ageFrom, LINKS } from "@/lib/api";
import { Brand } from "@/components/AppShell";
import { EmptyState } from "@/components/Field";
import { ProgressChart } from "@/components/ProgressChart";
import { DietPlanTable } from "@/components/DietPlanTable";
import { PathologyList } from "@/components/patient/PathologiesTab";
import { AppointmentList } from "@/components/patient/PatientAppointmentsTab";

const sectionFor = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("messaggio")) return "portal-chat";
  if (t.includes("piano")) return "portal-diet";
  if (t.includes("documento")) return "portal-documents";
  if (t.includes("scheda clinica")) return "portal-pathologies";
  if (t.includes("appuntamento") || t.includes("prenotata")) return "portal-appointments";
  if (t.includes("visita")) return "portal-progress";
  return null;
};

const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/me/notifications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const unread = items.filter((n) => !n.read).length;
  const onOpen = async (o) => {
    setOpen(o);
    if (!o && unread) {
      await api.post("/me/notifications/read-all");
      load();
    }
  };
  const goTo = (n) => {
    const el = document.getElementById(sectionFor(n.title));
    onOpen(false);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };
  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button className="relative rounded-full p-2.5 hover:bg-slate-100" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#3d655b] px-1 text-[10px] font-bold text-white" data-testid="notification-count">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-2xl p-0" data-testid="notification-panel">
        <div className="border-b px-4 py-3 font-bold">Notifiche</div>
        <div className="max-h-96 overflow-y-auto">
          {items.length ? items.map((n) => (
            <button type="button" key={n.id} onClick={() => goTo(n)} className={`block w-full border-b px-4 py-3 text-left transition-colors last:border-0 hover:bg-slate-50 ${n.read ? "" : "bg-[#edf2ef]/50"}`} data-testid={`notification-${n.id}`}>
              <div className="flex justify-between gap-2 text-sm font-semibold"><span>{n.title}</span>{!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#3d655b]" />}</div>
              <p className="text-xs text-slate-600">{n.message}</p>
              <div className="mt-1 flex justify-between text-[10px] text-slate-400"><span>{fmtDateTime(n.created_at)}</span>{sectionFor(n.title) && <span className="font-semibold text-[#3d655b]">Vai alla sezione →</span>}</div>
            </button>
          )) : <div className="px-4 py-6 text-center text-sm text-slate-400">Nessuna notifica</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Info = ({ label, value }) => (
  <div><div className="label-xs">{label}</div><div className="mt-1 text-sm font-semibold">{value || "—"}</div></div>
);

export default function PatientPortal() {
  const { logout } = useAuth();
  const [rec, setRec] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const loadRec = () => api.get("/me/record").then((r) => setRec(r.data));
  useEffect(() => { loadRec(); }, []);
  if (!rec) return <div className="min-h-screen" />;
  const p = rec.patient;
  const last = rec.visits[rec.visits.length - 1];

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="patient-portal">
      <header className="no-print sticky top-0 z-30 border-b border-[#e2e8f0] bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-8">
          <Brand />
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button onClick={logout} className="btn-ghost" data-testid="patient-logout-button"><LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Esci</span></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-10 px-4 py-8 sm:px-8">
        <section className="no-print fade-up">
          <div className="label-xs text-[#3d655b]">La tua area personale</div>
          <h1 className="page-title mt-1" data-testid="patient-portal-name">Ciao {p.first_name}</h1>
          <div className="card mt-6 grid grid-cols-2 gap-6 sm:grid-cols-5">
            <Info label="Nome" value={fullName(p)} />
            <Info label="Età" value={ageFrom(p.birth_date) != null ? `${ageFrom(p.birth_date)} anni` : ""} />
            <Info label="Altezza" value={(() => { const h = [...rec.visits].reverse().find((v) => v.height)?.height || p.height_cm; return h ? `${h} cm` : ""; })()} />
            <Info label="Peso attuale" value={last?.weight ? `${last.weight} kg` : ""} />
            <Info label="Ultima visita" value={fmtDate(last?.date)} />
          </div>
        </section>

        <section id="portal-progress" className="no-print scroll-mt-24 space-y-4">
          <h2 className="flex items-center gap-2 text-xl font-bold"><Scale className="h-5 w-5 text-[#3d655b]" /> I tuoi progressi</h2>
          {rec.visits.length ? <ProgressChart visits={rec.visits} prefix="patient-progress" /> : <EmptyState title="Nessuna misurazione ancora" testid="portal-visits-empty" />}
        </section>

        <section className="no-print grid gap-8 lg:grid-cols-2">
          <div id="portal-appointments" className="scroll-mt-24 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-xl font-bold"><CalendarDays className="h-5 w-5 text-[#3d655b]" /> Appuntamenti</h2>
              <button className="btn-primary" onClick={() => setBookOpen(true)} data-testid="portal-book-visit-button"><CalendarDays className="h-4 w-4" /> Prenota visita</button>
            </div>
            <BookVisitDialog open={bookOpen} onOpenChange={setBookOpen} onBooked={loadRec} />
            {rec.appointments.length ? <AppointmentList items={rec.appointments} prefix="portal-appt" showCalendar /> : <EmptyState title="Nessun appuntamento" testid="portal-appointments-empty" />}
          </div>
          <div id="portal-pathologies" className="scroll-mt-24 space-y-4">
            <h2 className="flex items-center gap-2 text-xl font-bold"><HeartPulse className="h-5 w-5 text-[#3d655b]" /> Patologie</h2>
            {rec.pathologies.length ? <PathologyList items={rec.pathologies} prefix="portal-pathology" /> : <EmptyState title="Nessuna patologia registrata" testid="portal-pathologies-empty" />}
          </div>
        </section>

        <section id="portal-chat" className="no-print scroll-mt-24 space-y-4" data-testid="portal-chat-section">
          <h2 className="flex items-center gap-2 text-xl font-bold"><MessagesSquare className="h-5 w-5 text-[#3d655b]" /> Messaggi con la dottoressa</h2>
          <ChatBox url="/me/messages" me="patient" prefix="portal-chat" />
        </section>

        <section id="portal-documents" className="no-print scroll-mt-24 space-y-4" data-testid="portal-documents-section">
          <h2 className="flex items-center gap-2 text-xl font-bold"><Paperclip className="h-5 w-5 text-[#3d655b]" /> Documenti ed esami</h2>
          {rec.attachments?.length ? <AttachmentList items={rec.attachments} prefix="portal-attachment" /> : <EmptyState title="Nessun documento condiviso" testid="portal-documents-empty" />}
        </section>

        <section id="portal-diet" className="scroll-mt-24 space-y-4" data-testid="portal-diet-section">
          <div className="no-print flex items-center justify-between">
            <h2 className="text-xl font-bold">Il tuo piano nutrizionale</h2>
            {rec.diet_plan && <button className="btn-outline" onClick={() => window.print()} data-testid="portal-diet-print"><Printer className="h-4 w-4" /> Stampa</button>}
          </div>
          {rec.diet_plan ? (
            <div className="card print-area"><DietPlanTable plan={rec.diet_plan} patientName={fullName(p)} prefix="portal-diet" /></div>
          ) : (
            <EmptyState icon={Lock} title="Piano non ancora disponibile" text="La dottoressa lo renderà visibile qui appena pronto." testid="portal-diet-hidden" />
          )}
        </section>

        <footer className="no-print flex flex-wrap items-center justify-between gap-4 border-t border-[#e2e8f0] pt-6 text-sm text-slate-500">
          <span>Dott.ssa Simona Marretta · Biologa Nutrizionista</span>
          <div className="flex gap-4">
            <a href={LINKS.site} target="_blank" rel="noreferrer" className="hover:text-[#3d655b]" data-testid="portal-link-site">Sito</a>
            <a href={LINKS.instagram} target="_blank" rel="noreferrer" className="hover:text-[#3d655b]" data-testid="portal-link-instagram">Instagram</a>
            <a href={LINKS.review} target="_blank" rel="noreferrer" className="font-semibold text-[#3d655b]" data-testid="portal-link-review">Lascia una recensione</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
